import React, { useEffect, useState } from 'react';
import { useUser } from '@store';
import { streamChatService } from '../services/streamChatService';
import { tokenService } from '../services/tokenService';
import { useChatStore } from '../store/chatStore';
import { useStreamChatEvents } from '../hooks/useStreamChatEvents';
import ChatNotificationProvider from './ChatNotification';
import { useFeaturePermission, usePermissionsLoaded, FeaturePermission } from '@utils/featurePermissions';

interface GlobalChatProviderProps {
  children: React.ReactNode;
}

/**
 * GlobalChatProvider - Lightweight chat connection manager for the entire app
 *
 * Unlike ChatProvider, this component:
 * - Only handles connection and event listeners (no UI)
 * - Runs globally across all pages
 * - Maintains unread counts for the navigation badge
 * - Doesn't render Stream Chat UI components
 */
const GlobalChatProvider: React.FC<GlobalChatProviderProps> = ({ children }) => {
  const user = useUser();
  const [isConnecting, setIsConnecting] = useState(false);
  const permissionsLoaded = usePermissionsLoaded();
  const hasChatPermission = useFeaturePermission(FeaturePermission.DISPATCHING_CHAT);

  const { isConnected, currentUser, setConnectionState, setCurrentUser, clearCurrentUser, updateTotalUnreadCount } =
    useChatStore();

  // Set up real-time event listeners for unread counts
  useStreamChatEvents();

  // Initialize Stream Chat connection when user changes
  useEffect(() => {
    const initializeChat = async () => {
      // Held in its own const rather than read off `user` again below, so the guard narrows it to
      // non-null for the connect path.
      const profileId = user?.profile?.id;

      if (!profileId || !permissionsLoaded || !hasChatPermission) {
        // Only disconnect when we can confirm the user lacks access:
        // - no authenticated user (logged out), OR
        // - permissions have finished loading and chat is not included
        // Do NOT disconnect while permissionsLoaded is still false — that would
        // cause an unnecessary disconnect/reconnect cycle on every page refresh
        // when isConnected is restored from persisted store state.
        const shouldDisconnect = isConnected && (!profileId || (permissionsLoaded && !hasChatPermission));
        if (shouldDisconnect) {
          try {
            await streamChatService.disconnectUser();
            clearCurrentUser();
            setConnectionState(false);
          } catch (err) {
            console.error('[GlobalChatProvider] Failed to disconnect:', err);
          }
        }
        return;
      }

      const firebaseUserId = profileId.toString();

      // Use user ID only (no role prefix) - matches backend token generation
      const streamUserId = firebaseUserId;

      // Skip if already connected to the same user
      if (isConnected && currentUser?.id === streamUserId) {
        return;
      }

      setIsConnecting(true);

      try {
        console.log('[GlobalChatProvider] Connecting user to Stream Chat:', streamUserId);

        // Create token provider function
        // Note: The role parameter is not used by the backend - it extracts the role
        // from the Firebase JWT claims. We pass 'admin' here for consistency but
        // the actual role is determined server-side from the user's database record.
        const tokenProvider = tokenService.getTokenProvider(firebaseUserId, 'admin');

        // Connect user with Stream Chat
        // Note: We don't pass userData here because the backend has already created
        // the user with the correct display_name, email, role, and region_id.
        // Passing userData here would overwrite the backend-created user data.
        const streamUser = await streamChatService.connectUser(streamUserId, tokenProvider);

        // Update store state
        setCurrentUser(streamUser as any);
        setConnectionState(true);

        console.log('[GlobalChatProvider] Stream Chat connection successful');

        // Load initial unread counts
        try {
          const client = streamChatService.getClient();
          const { total_unread_count } = await client.getUnreadCount();
          if (total_unread_count > 0) {
            updateTotalUnreadCount();
            console.log('[GlobalChatProvider] Loaded initial unread count:', total_unread_count);
          }
        } catch (err) {
          console.warn('[GlobalChatProvider] Failed to load initial unread counts:', err);
          // Don't fail the connection for this
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to connect to chat';
        console.error('[GlobalChatProvider] Connection failed:', err);

        setConnectionState(false, errorMessage);
      } finally {
        setIsConnecting(false);
      }
    };

    initializeChat();
  }, [
    user?.profile?.id,
    permissionsLoaded,
    hasChatPermission,
    isConnected,
    currentUser?.id,
    setConnectionState,
    setCurrentUser,
    clearCurrentUser,
    updateTotalUnreadCount,
  ]);

  // Log connection status for debugging
  useEffect(() => {
    if (isConnected) {
      console.log('[GlobalChatProvider] Chat system active - unread counts will update in real-time');
    }
  }, [isConnected]);

  // Wrap children with notification provider for global toast notifications
  return <ChatNotificationProvider>{children}</ChatNotificationProvider>;
};

export default GlobalChatProvider;
